/**
 * RoleSelector — enriched MUI Select for choosing a tenant role.
 *
 * Shows the role name as the primary label and the role description
 * (from the API) as a secondary line so admins can make an informed
 * choice without opening the full Roles page. Works as a drop-in
 * replacement for the raw <Select> in UsersPage and the invite dialog.
 *
 * Props match the MUI FormControl pattern so the caller keeps full
 * control of error state, required marker, and margin.
 */
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';

export type RoleSelectorRole = {
  id: string;
  name: string;
  description?: string | null;
  is_system?: boolean;
};

type Props = {
  value: string;
  onChange: (roleId: string) => void;
  roles: RoleSelectorRole[];
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

export function RoleSelector({
  value,
  onChange,
  roles,
  label = 'Role',
  required = false,
  error = false,
  helperText,
  disabled = false,
  sx,
}: Props) {
  const handleChange = (e: SelectChangeEvent<string>) => onChange(e.target.value);

  // Split system roles (seeded) from custom roles so admins know which
  // they can edit. System roles get a small chip.
  const systemRoles = roles.filter((r) => r.is_system);
  const customRoles = roles.filter((r) => !r.is_system);

  const renderRole = (role: RoleSelectorRole) => (
    <MenuItem key={role.id} value={role.id} sx={{ alignItems: 'flex-start', py: 1 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {role.name}
          </Typography>
          {role.is_system && (
            <Chip label="System" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
          )}
        </Box>
        {role.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {role.description}
          </Typography>
        )}
      </Box>
    </MenuItem>
  );

  const hasGroups = systemRoles.length > 0 && customRoles.length > 0;

  return (
    <FormControl fullWidth required={required} error={error} disabled={disabled} sx={sx}>
      <InputLabel>{label}</InputLabel>
      <Select value={value} onChange={handleChange} label={label}>
        {hasGroups ? (
          [
            <ListSubheader key="sys-header">System roles</ListSubheader>,
            ...systemRoles.map(renderRole),
            <ListSubheader key="custom-header">Custom roles</ListSubheader>,
            ...customRoles.map(renderRole),
          ]
        ) : (
          roles.map(renderRole)
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
